import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BriefcaseBusiness, ChevronDown, PlusCircle } from 'lucide-react';
import { useVenue } from '../../contexts/VenueContext';
import { useVenueSwitcher } from '../../hooks/useVenueSwitcher';
import { getVenueRoleBadge } from '../../utils/venueRoleBadge';
import NotificationBellButton from '../NotificationBellButton';
import SafeAvatarImage from '../SafeAvatarImage';
import ThemeToggle from '../ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const font = "'Inria Serif', serif";

const resolvePhotoUrl = (venue) => {
  const url = venue?.photo_urls?.[0] || venue?.main_photo || venue?.photo_url || venue?.image_url;
  if (!url) return null;
  return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
};

const resolveVenueName = (venue) => (
  venue?.map_name || venue?.restaurant_name || venue?.name || 'Venue'
).trim();

const venueInitialsOf = (name) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase() || 'V';

export default function StaffVenueHeader({ roleLabel = 'Venue staff', testIdPrefix = 'staff' }) {
  const { activeVenue, activeVenueAppId } = useVenue();
  const { venues, selectVenue, switching } = useVenueSwitcher();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  // Theme toggle is global (it lives in ThemeContext) but per product call
  // we only surface the toggle on the dedicated profile screen for the
  // staff/admin venue shells — not on every page that happens to use this
  // header. Matches the venue-owner shell's VenueHeader behaviour.
  const showThemeToggle = location.pathname === '/staff-admin/profile'
    || location.pathname === '/staff-app/profile';
  const photoUrl = resolvePhotoUrl(activeVenue);
  const venueName = resolveVenueName(activeVenue);
  const venueInitials = venueInitialsOf(venueName);

  const handleAddVenue = () => {
    const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...existing,
      isAddingVenue: true,
      returnPath: `${location.pathname}${location.search}`,
      returnVenueAppId: activeVenueAppId || null,
    }));
    setShowDropdown(false);
    navigate('/venue/intro');
  };

  return (
    <div
      data-testid={`${testIdPrefix}-venue-header`}
      style={{ width: '100%', maxWidth: 390, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0', position: 'relative' }}
    >
      <div style={{ position: 'relative', zIndex: 60, minWidth: 0, flex: 1, marginRight: 12 }}>
        <button
          onClick={() => setShowDropdown((prev) => !prev)}
          data-testid={`${testIdPrefix}-venue-selector-btn`}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0, maxWidth: '100%', cursor: 'pointer' }}
        >
          <div
            data-testid={`${testIdPrefix}-venue-photo`}
            style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', backgroundColor: 'var(--hairline)', border: '2px solid var(--surface)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}
          >
            <SafeAvatarImage
              src={photoUrl}
              alt={venueName}
              dataTestId={`${testIdPrefix}-venue-photo-image`}
              fallback={(
                <div data-testid={`${testIdPrefix}-venue-photo-fallback`} style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #F2DDB7 0%, #E3C28A 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: font, fontSize: 14, fontWeight: 800, color: '#5F4D34', letterSpacing: 0 }}>{venueInitials}</span>
                </div>
              )}
            />
          </div>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <span
              data-testid={`${testIdPrefix}-venue-name`}
              style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {venueName}
              <ChevronDown size={16} color="var(--text)" style={{ flexShrink: 0 }} />
            </span>
            <span
              data-testid={`${testIdPrefix}-venue-role-pill`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, width: 'fit-content', maxWidth: '100%', padding: '3px 9px', borderRadius: 999, background: 'var(--accent-surface-soft)', color: 'var(--accent-purple)', fontFamily: font, fontSize: 12, fontWeight: 700 }}
            >
              <BriefcaseBusiness size={12} strokeWidth={2.2} />
              {roleLabel}
            </span>
          </div>
        </button>

        {showDropdown && (
          <div data-testid={`${testIdPrefix}-venue-selector-dropdown`} style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 6,
            backgroundColor: 'var(--surface)', borderRadius: 14, padding: '8px 6px',
            boxShadow: 'var(--shadow-2)',
            minWidth: 220, maxHeight: 280, overflowY: 'auto',
            zIndex: 200,
          }}>
            {venues.map((v) => (
              <button
                key={v.id}
                onClick={() => { selectVenue(v); setShowDropdown(false); }}
                disabled={switching}
                data-testid={`${testIdPrefix}-venue-selector-option-${v.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', width: '100%',
                  background: activeVenue?.id === v.id ? 'var(--accent-surface-soft)' : 'none',
                  border: 'none', cursor: switching ? 'not-allowed' : 'pointer', borderRadius: 10,
                  opacity: switching ? 0.6 : 1,
                }}
              >
                <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', backgroundColor: 'var(--surface-2)', flexShrink: 0, border: '2px solid var(--surface)' }}>
                  <SafeAvatarImage
                    src={resolvePhotoUrl(v)}
                    alt={resolveVenueName(v)}
                    dataTestId={`${testIdPrefix}-venue-selector-photo-image-${v.id}`}
                    fallback={(
                      <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #F2DDB7 0%, #E3C28A 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontFamily: font, fontSize: 13, fontWeight: 800, color: '#5F4D34' }}>{venueInitialsOf(resolveVenueName(v))}</span>
                      </div>
                    )}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
                  <span style={{ fontFamily: font, fontSize: 17, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                    {v.restaurant_name || 'Venue'}
                  </span>
                  <span
                    data-testid={`${testIdPrefix}-venue-selector-role-badge-${v.id}`}
                    style={{ fontFamily: font, fontSize: 11, fontWeight: 700, color: 'var(--accent-purple)', background: 'var(--accent-surface-soft)', padding: '1px 8px', borderRadius: 999 }}
                  >
                    {getVenueRoleBadge(v)}
                  </span>
                </div>
              </button>
            ))}
            <button
              onClick={handleAddVenue}
              data-testid={`${testIdPrefix}-venue-add-btn`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '9px 10px', marginTop: 8,
                fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--accent-purple-strong)',
                background: 'var(--accent-surface-soft)', border: 'none', borderRadius: 12,
                cursor: 'pointer', transition: 'background-color 0.15s ease, transform 0.1s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-surface)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-surface-soft)'; }}
            >
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PlusCircle size={18} color="var(--accent-purple-strong)" />
              </span>
              Add your own venue
            </button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {showThemeToggle && <ThemeToggle />}
        <NotificationBellButton buttonTestId={`${testIdPrefix}-venue-header-bell-button`} popupTestId={`${testIdPrefix}-venue-header-notification-popup`} />
      </div>
    </div>
  );
}
